import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getReferrals } from "@/api/mockData";
import { t, formatGNK, type Lang } from "@/lib/i18n";

interface ReferralsProps {
  lang: Lang;
}

export function Referrals({ lang }: ReferralsProps) {
  const data = getReferrals();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(data.link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="pt-3">
        <h1 className="font-display text-3xl font-black tracking-tight">{t("ref.title", lang)}</h1>
        <p className="text-muted-foreground text-sm mt-1.5">{t("ref.subtitle", lang)}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="glass-card rounded-2xl p-4 text-center animate-fade-in-up border border-primary/12 relative overflow-hidden" style={{ animationDelay: "0ms" }}>
          <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg, rgba(220,38,38,0.6), transparent)" }} />
          <p className="text-[32px] font-black font-display text-primary leading-none">{data.totalInvited}</p>
          <p className="text-[8px] uppercase tracking-widest text-muted-foreground/60 font-medium mt-2">{t("ref.invited", lang)}</p>
        </div>
        <div className="glass-card rounded-2xl p-4 text-center animate-fade-in-up border border-chart-2/12 relative overflow-hidden" style={{ animationDelay: "60ms" }}>
          <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg, rgba(220,38,38,0.4), transparent)" }} />
          <p className="text-[32px] font-black font-display text-chart-2 leading-none">{formatGNK(data.totalEarned)}</p>
          <p className="text-[8px] uppercase tracking-widest text-muted-foreground/60 font-medium mt-2">{t("ref.earned", lang)}</p>
        </div>
        <div className="glass-card rounded-2xl p-4 text-center animate-fade-in-up border border-chart-3/12 relative overflow-hidden" style={{ animationDelay: "120ms" }}>
          <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg, rgba(220,38,38,0.3), transparent)" }} />
          <p className="text-[32px] font-black font-display text-chart-3 leading-none">{data.bonusPerReferral}</p>
          <p className="text-[8px] uppercase tracking-widest text-muted-foreground/60 font-medium mt-2">GNK/{(t("ref.bonus", lang).split(" ")[0] ?? "").toLowerCase()}</p>
        </div>
      </div>

      {/* Referral Tier Progress */}
      <div className="glass-card rounded-2xl p-5 animate-fade-in-up" style={{ animationDelay: "180ms" }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
            {lang === "ru" ? "Прогресс реферала" : "Referral Tier"}
          </span>
          <span className="text-[10px] font-mono-num font-bold text-primary">
            {lang === "ru" ? "Уровень 2" : "Tier 2"} → {lang === "ru" ? "Уровень 3" : "Tier 3"}
          </span>
        </div>
        <div className="h-2.5 bg-muted/40 rounded-full overflow-hidden">
          <div className="progress-bar h-full" style={{ width: "70%" }} />
        </div>
        <p className="text-[10px] text-muted-foreground/80 mt-2">
          {lang === "ru"
            ? "Пригласите ещё 3 друзей для Уровня 3 (30 GNK/реф)"
            : "Invite 3 more friends for Tier 3 (30 GNK/ref)"}
        </p>
      </div>

      {/* Referral Link — prominent invite card */}
      <div className="glass-card-featured rounded-2xl overflow-hidden">
        <div className="h-[2px] bg-gradient-to-r from-primary via-primary/50 to-transparent" />
        <div className="p-5 space-y-4">
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground font-medium">{t("ref.link", lang)}</p>
          <div className="flex gap-2">
            <div className="flex-1 bg-secondary/30 rounded-xl px-3 py-3 font-mono-num text-[11px] text-muted-foreground truncate border border-white/[0.04]">
              {data.link}
            </div>
            <button
              onClick={handleCopy}
              className={`px-5 rounded-xl font-bold text-xs shrink-0 transition-all duration-300 ${
                copied
                  ? "bg-chart-3/20 text-chart-3 border border-chart-3/30"
                  : "cta-primary"
              }`}
            >
              {copied ? t("ref.copied", lang) : t("ref.copy", lang)}
            </button>
          </div>
          <button
            onClick={handleCopy}
            className="w-full h-[56px] rounded-2xl cta-primary font-display flex items-center justify-center gap-2.5"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            {t("ref.share", lang)}
          </button>
          <p className="text-[10px] text-center text-muted-foreground/60">
            {lang === "ru"
              ? `Код: ${data.code} — вы получаете ${data.bonusPerReferral} GNK за каждого приглашённого`
              : `Code: ${data.code} — you earn ${data.bonusPerReferral} GNK per referral`}
          </p>
        </div>
      </div>

      {/* Referral List */}
      <section>
        <h2 className="section-label mb-4">
          {t("ref.friends", lang)}
        </h2>
        <Card className="glass-card border-0 rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            {data.referrals.map((ref, i) => (
              <div
                key={ref.id}
                className={`flex items-center gap-3 px-4 py-3.5 animate-fade-in-up ${
                  i !== data.referrals.length - 1 ? "border-b border-white/[0.03]" : ""
                }`}
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <div className="w-9 h-9 rounded-xl bg-secondary/60 flex items-center justify-center text-xs font-bold text-primary border border-white/[0.04]">
                  {ref.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold truncate">{ref.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-muted-foreground/70">{ref.joinDate}</span>
                    <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/8 text-primary border border-primary/12">
                      {t("ref.level", lang)} {ref.level}
                    </span>
                  </div>
                </div>
                <span className="font-mono-num text-[13px] font-bold text-chart-2">
                  +{formatGNK(ref.bonusEarned)}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
